import { PropsWithChildren } from 'react';
import { makeStyles, Box } from '@material-ui/core';
import HomeIcon from '@material-ui/icons/Home';
import ExtensionIcon from '@material-ui/icons/Extension';
import LibraryBooks from '@material-ui/icons/LibraryBooks';
import CreateComponentIcon from '@material-ui/icons/AddCircleOutline';
import AssessmentIcon from '@material-ui/icons/Assessment';
import AccountTreeIcon from '@material-ui/icons/AccountTree';
import LogoFull from './LogoFull';
import LogoIcon from './LogoIcon';

import {
  Sidebar,
  sidebarConfig,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarPage,
  SidebarScrollWrapper,
  SidebarSpace,
  SidebarSubmenu,
  SidebarSubmenuItem,
  useSidebarOpenState,
  Link,
} from '@backstage/core-components';
import MenuIcon from '@material-ui/icons/Menu';
import BuildIcon from '@material-ui/icons/Build';
import BugReportIcon from '@material-ui/icons/BugReport';
import CategoryIcon from '@material-ui/icons/Category';
import { NotificationsSidebarItem } from '@backstage/plugin-notifications';
import AdminPanelSettingsIcon from '@material-ui/icons/Security';
import VerifiedUserIcon from '@material-ui/icons/VerifiedUser';
import StorageIcon from '@material-ui/icons/Storage';
import DeviceHubIcon from '@material-ui/icons/DeviceHub';
import BarChartIcon from '@material-ui/icons/BarChart';
import { AiSparkleIcon } from './AiSparkleIcon';
import { GlobalHeader, useRoleContext } from '../GlobalHeader';

const useSidebarLogoStyles = makeStyles({
  root: {
    width: '100%',
    height: 3 * sidebarConfig.logoHeight,
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: -14,
  },
  link: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
});

const SidebarLogo = () => {
  const classes = useSidebarLogoStyles();
  const { isOpen } = useSidebarOpenState();

  return (
    <div className={classes.root}>
      <Link to="/" underline="none" className={classes.link} aria-label="Home">
        {isOpen ? <LogoFull /> : <LogoIcon />}
      </Link>
    </div>
  );
};

const PlatformAdminSidebarItem = () => {
  const { isPlatformLead } = useRoleContext();
  if (!isPlatformLead) return null;
  return (
    <SidebarItem
      icon={AdminPanelSettingsIcon}
      to="create/templates/default/manage-platform-lead-group"
      text="Platform Admin"
    />
  );
};

const RunnerHealthSidebarItem = () => {
  const { isPlatformLead } = useRoleContext();
  if (!isPlatformLead) return null;
  return (
    <SidebarItem
      icon={StorageIcon}
      to="platform-ops/runner-health"
      text="Runner Health"
    />
  );
};

const CopilotAdminSidebarItem = () => {
  const { isPlatformLead } = useRoleContext();
  if (!isPlatformLead) return null;
  return (
    <SidebarItem
      icon={BarChartIcon}
      to="copilot-admin"
      text="Copilot Analytics"
    />
  );
};

export const Root = ({ children }: PropsWithChildren<{}>) => (
  <SidebarPage>
    <Sidebar>
      <SidebarLogo />
      <SidebarDivider />
      <SidebarGroup label="Menu" icon={<MenuIcon />}>
        {/* Global nav, not org-specific */}
        <SidebarItem icon={HomeIcon} to="/" text="Home" />
        <SidebarItem icon={CategoryIcon} to="catalog" text="Catalog" />
        <SidebarItem icon={ExtensionIcon} to="api-docs" text="APIs" />
        <SidebarItem icon={LibraryBooks} to="docs" text="Docs" />
        <SidebarItem icon={CreateComponentIcon} to="create" text="Create..." />
        <SidebarItem icon={AiSparkleIcon} text="Agentic AI">
          <SidebarSubmenu title="Agentic AI">
            <SidebarSubmenuItem icon={AiSparkleIcon} to="ai-agent" title="AI Agent" />
            <SidebarSubmenuItem icon={AssessmentIcon} to="agent-registry" title="Agent Registry" />
            <SidebarSubmenuItem icon={DeviceHubIcon} to="tools-registry" title="Tools Registry" />
            <SidebarSubmenuItem icon={AccountTreeIcon} to="workflow-composer" title="Workflow Composer" />
            <SidebarSubmenuItem icon={AssessmentIcon} to="platform-studio" title="Platform Studio" />
            <SidebarSubmenuItem icon={ExtensionIcon} to="copilot-extensions" title="Copilot Extensions" />
            <SidebarSubmenuItem icon={AssessmentIcon} to="copilot-admin" title="Copilot Analytics" />
          </SidebarSubmenu>
        </SidebarItem>
        <SidebarItem icon={VerifiedUserIcon} text="Security">
          <SidebarSubmenu title="Security">
            <SidebarSubmenuItem icon={AssessmentIcon} to="quality-studio" title="Quality Studio" />
          </SidebarSubmenu>
        </SidebarItem>
        {/* End global nav */}
        <SidebarDivider />
        <SidebarScrollWrapper>
          {/* Items in this group will be scrollable if they run out of space */}
        </SidebarScrollWrapper>
      </SidebarGroup>
      <SidebarSpace />
      <SidebarDivider />
      <SidebarItem icon={BuildIcon} to="devtools" text="DevTools" />
      <SidebarItem icon={BugReportIcon} to="catalog-unprocessed" text="Unprocessed" />
      <NotificationsSidebarItem />
      <PlatformAdminSidebarItem />
      <RunnerHealthSidebarItem />
      <CopilotAdminSidebarItem />
      <SidebarItem icon={ExtensionIcon} to="copilot-extensions" text="Extensions" />
    </Sidebar>
    <Box style={{ flex: 1, height: '100vh', overflow: 'auto' }}>
      <GlobalHeader />
      {children}
    </Box>
  </SidebarPage>
);
